"use client";

import { useLocale, useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";
import { mockupsFor } from "@/lib/brand";
import { cn } from "@/lib/utils";
import { MockupPicture } from "@/components/landing/mockup-picture";

const tagKeys = [1, 2, 3, 4, 5, 6] as const;

export function LandingShowcaseCarousel() {
  const t = useTranslations("landing");
  const { brand } = useSiteFlavor();
  const mockups = mockupsFor(brand, useLocale());
  const { resolvedTheme } = useTheme();
  const textRef = useScrollReveal<HTMLDivElement>({ animation: "fade-in-left" });
  const imageRef = useScrollReveal<HTMLDivElement>({ animation: "fade-in-right" });

  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div
          className={cn(
            "grid items-center gap-12",
            // Without the mockup the copy centres instead of hugging a
            // half-width column with nothing beside it.
            mockups.editor ? "md:grid-cols-2" : "mx-auto max-w-2xl text-center",
          )}
        >
          {/* Text */}
          <div ref={textRef}>
            <h2 className="text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {t("showcaseCarousel.heading1")}
              <br />
              <span className="font-serif italic text-mint">
                {t("showcaseCarousel.heading2")}
              </span>
            </h2>
            <p className="mt-4 text-landing-muted leading-relaxed">
              {t("showcaseCarousel.description")}
            </p>
            <div
              className={cn(
                "mt-6 flex flex-wrap gap-2",
                !mockups.editor && "justify-center",
              )}
            >
              {tagKeys.map((n) => (
                <span
                  key={n}
                  className="rounded-full border border-landing-border bg-landing-card px-3 py-1 text-xs text-landing-muted"
                >
                  {t(`showcaseCarousel.tag${n}`)}
                </span>
              ))}
            </div>
          </div>

          {/* Phone mockup — omitted on surfaces with no screenshots of their
              own rather than falling back to another brand's. */}
          {mockups.editor && (
            <div className="flex justify-center" ref={imageRef}>
              <MockupPicture
                src={resolvedTheme === "dark" ? mockups.editor.dark : mockups.editor.light}
                alt={t("showcaseCarousel.mockupAlt")}
                // Height-constrained, so the painted width is the aspect ratio
                // (1320/2346) times the height — ~236px, ~293px at sm.
                sizes="(max-width: 640px) 236px, 293px"
                className={resolvedTheme === "dark" ? "h-[420px] w-auto rounded-3xl sm:h-[520px]" : "h-[420px] w-auto sm:h-[520px]"}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
